import * as Schema from "effect/Schema";

import { TrimmedNonEmptyString, TrimmedString } from "./baseSchemas";

const WORKSPACE_DIRECTORY_PATH_MAX_LENGTH = 512;

export const WorkspaceDirectoryEntryKind = Schema.Literals(["file", "directory"]);
export type WorkspaceDirectoryEntryKind = typeof WorkspaceDirectoryEntryKind.Type;

/**
 * One immediate child of a listed workspace directory.
 *
 * `hasChildren` is what lets the Files tree draw an expander only on
 * directories that open onto something, without listing the whole workspace
 * up front: a directory entry is one stat-sized readdir, not a recursive walk.
 */
export const WorkspaceDirectoryEntry = Schema.Struct({
  name: TrimmedNonEmptyString,
  kind: WorkspaceDirectoryEntryKind,
  hasChildren: Schema.Boolean,
});
export type WorkspaceDirectoryEntry = typeof WorkspaceDirectoryEntry.Type;

export const WorkspaceListDirectoryInput = Schema.Struct({
  /** The workspace root the listing is confined to; the thread's checkout. */
  cwd: TrimmedNonEmptyString,
  /**
   * Workspace-root-relative directory to list. Empty means the root itself.
   * Absolute paths and traversal outside the root are refused.
   */
  relativePath: TrimmedString.check(Schema.isMaxLength(WORKSPACE_DIRECTORY_PATH_MAX_LENGTH)),
});
export type WorkspaceListDirectoryInput = typeof WorkspaceListDirectoryInput.Type;

export const WorkspaceListDirectoryResult = Schema.Struct({
  entries: Schema.Array(WorkspaceDirectoryEntry),
});
export type WorkspaceListDirectoryResult = typeof WorkspaceListDirectoryResult.Type;

export const WorkspaceListDirectoryFailure = Schema.Literals([
  "workspace_root_invalid",
  "path_outside_workspace",
  "read_directory_failed",
]);
export type WorkspaceListDirectoryFailure = typeof WorkspaceListDirectoryFailure.Type;

export class WorkspaceListDirectoryError extends Schema.TaggedErrorClass<WorkspaceListDirectoryError>()(
  "WorkspaceListDirectoryError",
  {
    cwd: Schema.String,
    relativePath: Schema.String,
    failure: WorkspaceListDirectoryFailure,
    detail: Schema.String,
    cause: Schema.optional(Schema.Defect),
  },
) {
  // fallow-ignore-next-line unused-class-member
  override get message(): string {
    return this.detail;
  }
}
